import type { KataScoringDefinition, ScoringCriterion } from './types'

function c(
  id: string,
  text: string,
  suggested: ScoringCriterion['suggested'],
  hint?: string,
): ScoringCriterion {
  return hint ? { id, text, suggested, hint } : { id, text, suggested }
}

const generalCeremony: ScoringCriterion[] = [
  c('ju-gen-dist-tsuki-dashi', 'Distance: Tsuki-dashi: about 1.8 m', 'medium'),
  c(
    'ju-gen-dist-kata-oshi',
    'Distance: Kata-oshi: Tori’s left foot and Uke’s right lined up and close',
    'medium',
  ),
  c('ju-gen-dist-ryote-dori', 'Distance: Ryote-dori: about half step', 'medium'),
  c('ju-gen-dist-kata-mawashi', 'Distance: Kata-mawashi: about half step', 'medium'),
  c('ju-gen-dist-ago-oshi', 'Distance: Ago-oshi: about three steps', 'medium'),
  c('ju-gen-dist-kiri-oroshi', 'Distance: Kiri-oroshi: about two steps', 'medium'),
  c(
    'ju-gen-dist-ryokata-oshi',
    'Distance: Ryo-kata-oshi: arms length (Uke’s palms touch Tori’s shoulders)',
    'medium',
  ),
  c('ju-gen-dist-naname-uchi', 'Distance: Naname-uchi: about one step', 'medium'),
  c('ju-gen-dist-katate-dori', 'Distance: Katate-dori: Tori and Uke side by side', 'medium'),
  c('ju-gen-dist-katate-age', 'Distance: Katate-age: about 2.7 m', 'medium'),
  c('ju-gen-dist-obi-tori', 'Distance: Obi-tori: about one step', 'medium'),
  c('ju-gen-dist-mune-oshi', 'Distance: Mune-oshi: about one step (slightly narrower)', 'medium'),
  c('ju-gen-dist-tsuki-age', 'Distance: Tsuki-age: about one step', 'medium'),
  c('ju-gen-dist-uchi-oroshi', 'Distance: Uchi-oroshi: about two steps', 'medium'),
  c('ju-gen-dist-ryogan-tsuki', 'Distance: Ryogan-tsuki: about one step (slightly wider)', 'medium'),
  c(
    'ju-gen-submitting',
    'Submitting (tapping once with free arm or moving back one foot)',
    'small',
    'Medium if Uke doesn’t tap',
  ),
]

/** Practice judging: 16 scored elements (max 160); opening/closing bows are catalog-only. */
export const juNoKataScoring: KataScoringDefinition = {
  kataId: 'ju-no-kata',
  maxPerJudge: 160,
  maxPanelOfThree: 480,
  elements: [
    {
      id: 'ju-general',
      kind: 'ceremony',
      name: 'General elements',
      nameJa: '一般観察事項',
      criteria: generalCeremony,
    },
    {
      id: 'ju-tsuki-dashi',
      kind: 'technique',
      name: 'Tsuki-dashi',
      nameJa: '突出',
      techniqueId: 'ju-tsuki-dashi',
      groupId: 'dai-ikkyo',
      criteria: [
        c(
          'ju-tsuki-dashi-1',
          'Uke’s tsugi-ashi and gradually and continuously lift of the arm (belt, throat) hitting Tori’s uto on the third step.',
          'small_medium',
        ),
        c(
          'ju-tsuki-dashi-2',
          'Uke: attacks between Tori’s eyebrows on the third step. Tori takes Uke’s right wrist thumb down and the left wrist thumb up.',
          'small_medium',
        ),
        c(
          'ju-tsuki-dashi-3',
          'Tori and Uke way of freeing (opening and closing of the legs when turning, hand grips with thumb up).',
          'small_medium',
        ),
        c(
          'ju-tsuki-dashi-4',
          'Conclusion: breaking Uke’s balance backwards (Uke’s right arm stretched).',
          'small_medium',
        ),
      ],
    },
    {
      id: 'ju-kata-oshi',
      kind: 'technique',
      name: 'Kata-oshi',
      nameJa: '肩押',
      techniqueId: 'ju-kata-oshi',
      groupId: 'dai-ikkyo',
      criteria: [
        c(
          'ju-kata-oshi-1',
          'Uke lifts his right hand and pushes on the back side of Tori’s right shoulder.',
          'small_medium',
        ),
        c(
          'ju-kata-oshi-2',
          'Tori bends down; Uke bends when Tori ducks and then pushes Tori’s shoulder backwards with his right palm; Tori moves backwards gripping Uke’s right four fingers with his right hand.',
          'small_medium',
        ),
        c('ju-kata-oshi-3', 'Uke prepares his left hand before the attack.', 'small_medium'),
        c(
          'ju-kata-oshi-4',
          'Uke strikes between Tori’s eyebrows with his left fingertips moving forward with his left foot and Tori’s counterattack by gripping first left hand fingers, then right, and extending both hands while moving backward.',
          'small_medium',
        ),
        c(
          'ju-kata-oshi-5',
          'Conclusion: Tori breaks Uke’s balance backwards with stretched arms.',
          'small_medium',
        ),
      ],
    },
    {
      id: 'ju-ryote-dori',
      kind: 'technique',
      name: 'Ryote-dori',
      nameJa: '両手取',
      techniqueId: 'ju-ryote-dori',
      groupId: 'dai-ikkyo',
      criteria: [
        c('ju-ryote-dori-1', 'Uke: grabs both of Tori’s wrists.', 'small_medium'),
        c(
          'ju-ryote-dori-2',
          'Tori: pulls his hands backwards and steps slightly backward with his left foot to break Uke’s balance forward and does technique (Soto-makikomi).',
          'small_medium',
        ),
        c(
          'ju-ryote-dori-3',
          'Loading of Tori and lifting of Uke with stretched legs.',
          'small_medium',
        ),
      ],
    },
    {
      id: 'ju-kata-mawashi',
      kind: 'technique',
      name: 'Kata-mawashi',
      nameJa: '肩廻',
      techniqueId: 'ju-kata-mawashi',
      groupId: 'dai-ikkyo',
      criteria: [
        c(
          'ju-kata-mawashi-1',
          'Uke: turns Tori’s shoulders (right hand on the back of right shoulder, left hand in front of the left shoulder).',
          'small_medium',
        ),
        c(
          'ju-kata-mawashi-2',
          'Tori: breaks Uke’s balance forward with his left hand and does technique (Ippon-seoi-nage, loading Uke onto his shoulders).',
          'small_medium',
        ),
        c(
          'ju-kata-mawashi-3',
          'Loading of Tori and lifting of Uke with stretched legs.',
          'small_medium',
        ),
      ],
    },
    {
      id: 'ju-ago-oshi',
      kind: 'technique',
      name: 'Ago-oshi',
      nameJa: '腮押',
      techniqueId: 'ju-ago-oshi',
      groupId: 'dai-ikkyo',
      criteria: [
        c('ju-ago-oshi-1', 'Tori: three steps forward (turning to right side with third step).', 'small_medium'),
        c(
          'ju-ago-oshi-2',
          'Uke in tsugi-ashi and gradually and continuously lift of the arm (belt, throat) attempting to push the right side of Tori’s chin with his right fingertips on the third step.',
          'small_medium',
        ),
        c(
          'ju-ago-oshi-3',
          'Tori’s defense: Tori turns his left foot, body and face to his left, grabs Uke’s right hand with his right hand putting his right thumb on Uke’s right palm, and moves forward with his right foot turning 180°.',
          'small_medium',
        ),
        c('ju-ago-oshi-4', 'Uke prepares the hand before the attack.', 'small_medium'),
        c(
          'ju-ago-oshi-5',
          'Uke strikes between Tori’s eyebrows with his left fingertips moving forward with his left foot and Tori counterattacks (hand grip) moving backward with his left foot.',
          'small_medium',
        ),
        c(
          'ju-ago-oshi-6',
          'Conclusion: Tori breaks Uke’s balance backward blocking with bent arms.',
          'small_medium',
        ),
      ],
    },
    {
      id: 'ju-kiri-oroshi',
      kind: 'technique',
      name: 'Kiri-oroshi',
      nameJa: '切下',
      techniqueId: 'ju-kiri-oroshi',
      groupId: 'dai-nikyo',
      criteria: [
        c(
          'ju-kiri-oroshi-1',
          'Uke: preparation of the attack (stepping backward with his right foot turning to his right and facing his right palm forward at the same time).',
          'small_medium',
        ),
        c(
          'ju-kiri-oroshi-2',
          'Uke: turns to face Tori, Te-gatana attack stepping forward with his right foot.',
          'small_medium',
        ),
        c(
          'ju-kiri-oroshi-3',
          'Tori: steps backward (right-left foot), grabs Uke’s right wrist, takes two tsugi-ashi steps forward to break Uke’s balance to the right rear corner.',
          'small_medium',
        ),
        c(
          'ju-kiri-oroshi-4',
          'Uke: pushes Tori’s right elbow to release the grip on his right wrist and turns Tori 180° in a big circle.',
          'small_medium',
        ),
        c(
          'ju-kiri-oroshi-5',
          'Tori: pivots on left foot and grabs Uke’s left hand with his left hand (from the bottom) putting his left thumb on Uke’s left palm.',
          'small_medium',
        ),
        c(
          'ju-kiri-oroshi-6',
          'Tori: continues behind Uke, breaking the balance backward.',
          'small_medium',
        ),
        c(
          'ju-kiri-oroshi-7',
          'Conclusion: Tori breaks Uke’s balance backwards with the left arm stretched.',
          'small_medium',
        ),
      ],
    },
    {
      id: 'ju-ryokata-oshi',
      kind: 'technique',
      name: 'Ryo-kata-oshi',
      nameJa: '両肩押',
      techniqueId: 'ju-ryokata-oshi',
      groupId: 'dai-nikyo',
      criteria: [
        c(
          'ju-ryokata-oshi-1',
          'Uke: attacks Tori from behind by pushing shoulders down (with the palms of his hands).',
          'small_medium',
        ),
        c(
          'ju-ryokata-oshi-2',
          'Tori’s defence: sinking the body, left foot half step backward, right foot half step leftward, turns, grabs Uke’s right wrist with his left hand, left foot half step backward (Uke half step forward), right foot a half step leftward, grabs Uke’s right wrist with his right hand.',
          'small_medium',
        ),
        c(
          'ju-ryokata-oshi-3',
          'Tori: turns, changes his grip on Uke’s right wrist, and moves forward, slowly raising the body, pulling Uke to his back.',
          'small_medium',
        ),
        c('ju-ryokata-oshi-4', 'Uke defends by pushing with left hand on Tori’s back.', 'small_medium'),
        c(
          'ju-ryokata-oshi-5',
          'Tori: turns to his left, moves back right-left foot (Uke’s heels come on the mat), placing his left upper arm across Uke’s chest, he pushes back.',
          'small_medium',
        ),
        c(
          'ju-ryokata-oshi-6',
          'Conclusion: Tori breaks Uke’s balance by pushing with left upper arm and lifting and stretching Uke’s right arm.',
          'small_medium',
        ),
      ],
    },
    {
      id: 'ju-naname-uchi',
      kind: 'technique',
      name: 'Naname-uchi',
      nameJa: '斜打',
      techniqueId: 'ju-naname-uchi',
      groupId: 'dai-nikyo',
      criteria: [
        c(
          'ju-naname-uchi-1',
          'Uke attempts a diagonal strike between Tori’s eyebrows with Te-gatana.',
          'small_medium',
        ),
        c(
          'ju-naname-uchi-2',
          'Tori avoids and grabs Uke’s wrist, then counterattacks with his right fingers (palm facing down).',
          'small_medium',
        ),
        c(
          'ju-naname-uchi-3',
          'Uke grabs Tori’s wrist, turns to his left, and pulls Tori forward. Tori frees the wrist grabbing and pushing Uke’s left wrist with his left hand and stepping forward with his left foot.',
          'small_medium',
        ),
        c('ju-naname-uchi-4', 'Uke places his hand on Tori’s elbow and forces Tori to turn.', 'small_medium'),
        c(
          'ju-naname-uchi-5',
          'Tori rotates and lifts Uke in Ura-nage (upper body leaning backward). Uke’s legs together and stretched arms above head.',
          'small_medium',
        ),
      ],
    },
    {
      id: 'ju-katate-dori',
      kind: 'technique',
      name: 'Katate-dori',
      nameJa: '片手取',
      techniqueId: 'ju-katate-dori',
      groupId: 'dai-nikyo',
      criteria: [
        c('ju-katate-dori-1', 'Uke: grabs Tori’s right wrist.', 'small_medium'),
        c(
          'ju-katate-dori-2',
          'Tori: lifts with a stretched arm (Kansetsu-waza) moving diagonally forward with his right foot.',
          'small_medium',
        ),
        c(
          'ju-katate-dori-3',
          'Uke moves forward in defence with his right foot pushing away Tori’s right elbow with his right palm and releasing his left hand to pull Tori’s left shoulder.',
          'small_medium',
        ),
        c('ju-katate-dori-4', 'Tori rotates and lifts Uke with Uki-goshi (to O-Goshi).', 'small_medium'),
      ],
    },
    {
      id: 'ju-katate-age',
      kind: 'technique',
      name: 'Katate-age',
      nameJa: '片手挙',
      techniqueId: 'ju-katate-age',
      groupId: 'dai-nikyo',
      criteria: [
        c(
          'ju-katate-age-1',
          'After raising their right arms and heels up, Tori and Uke approach each other.',
          'small_medium',
        ),
        c(
          'ju-katate-age-2',
          'Tori steps aside with his right foot avoiding collision, holding Uke’s left shoulder and right elbow; Uke reacts, Tori blocks Uke’s left elbow; Uke reacts again (Tori stands straight controlling Uke’s reaction; actions and reactions are slow and Tori and Uke are very close to each other).',
          'small_medium',
        ),
        c(
          'ju-katate-age-3',
          'Conclusion: Tori breaks Uke’s balance backwards with the right arm stretched.',
          'small_medium',
        ),
      ],
    },
    {
      id: 'ju-obi-tori',
      kind: 'technique',
      name: 'Obi-tori',
      nameJa: '帯取',
      techniqueId: 'ju-obi-tori',
      groupId: 'dai-sankyo',
      criteria: [
        c(
          'ju-obi-tori-1',
          'Uke: steps in with left foot forward (crossed arms left hand on top, palms facing down) and tries to grab Tori’s belt.',
          'small_medium',
        ),
        c(
          'ju-obi-tori-2',
          'Tori: grabs Uke’s left wrist with his right hand (the back of hand facing downward) and pulls to his left, then Uke’s left elbow with his left hand and pushes upwards; he then pulls Uke’s right shoulder with his right hand, while attempting to turn around Uke’s body to the right.',
          'small_medium',
        ),
        c(
          'ju-obi-tori-3',
          'Uke: rotating, pulls Tori’s right elbow sideward and pulls Tori’s left shoulder pushing Tori’s elbow upward.',
          'small_medium',
        ),
        c(
          'ju-obi-tori-4',
          'Tori: turns again and pulls Uke close on the hip to lift with Uki-goshi, then O-goshi.',
          'small_medium',
        ),
      ],
    },
    {
      id: 'ju-mune-oshi',
      kind: 'technique',
      name: 'Mune-oshi',
      nameJa: '胸押',
      techniqueId: 'ju-mune-oshi',
      groupId: 'dai-sankyo',
      criteria: [
        c(
          'ju-mune-oshi-1',
          'Uke pushes his right palm against Tori’s left chest, Tori leans backward with his chest and pushes Uke’s right wrist up with his left hand in Yahazu (V shape) and pushes his right hand against Uke’s left chest, Uke grabs Tori’s right wrist and they rotate towards Shomen changing grip as they go.',
          'small_medium',
        ),
        c(
          'ju-mune-oshi-2',
          'Tori: blocking Uke’s right arm and changing the grip on Uke’s straight stretched left arm (at the elbow), breaking Uke’s balance backward to the right rear corner.',
          'small_medium',
        ),
        c(
          'ju-mune-oshi-3',
          'Conclusion: Tori steps in right foot, then left foot, lowers hips and breaks Uke’s balance even more.',
          'medium',
        ),
      ],
    },
    {
      id: 'ju-tsuki-age',
      kind: 'technique',
      name: 'Tsuki-age',
      nameJa: '突上',
      techniqueId: 'ju-tsuki-age',
      groupId: 'dai-sankyo',
      criteria: [
        c(
          'ju-tsuki-age-1',
          'Uke: stepping back right foot, then forward again attempts uppercut to Tori’s chin.',
          'small_medium',
        ),
        c(
          'ju-tsuki-age-2',
          'Tori: leans backward to avoid Uke’s attack, catches fist in right hand, places his left palm to Uke’s right elbow and steps forward with his left foot and turns Uke.',
          'small_medium',
        ),
        c('ju-tsuki-age-3', 'Uke: pivots 360° on left foot.', 'small_medium'),
        c(
          'ju-tsuki-age-4',
          'Tori: pulls Uke’s right arm straight up grabbing Uke’s right elbow with his left hand, hooking around Uke’s shoulder in the form of Ude-garami, breaking Uke’s balance backwards the right rear corner. Tori steps in left-right foot.',
          'small_medium',
        ),
      ],
    },
    {
      id: 'ju-uchi-oroshi',
      kind: 'technique',
      name: 'Uchi-oroshi',
      nameJa: '打下',
      techniqueId: 'ju-uchi-oroshi',
      groupId: 'dai-sankyo',
      criteria: [
        c(
          'ju-uchi-oroshi-1',
          'Uke: lifting right fist and arm in a big circle, steps forward with right foot and attempts to strike the top of Tori’s head with the back of his right fist.',
          'small_medium',
        ),
        c(
          'ju-uchi-oroshi-2',
          'Tori: steps backward (right-left foot), grabs Uke’s right wrist with his right hand (from the top in a regular grip), takes two tsugi-ashi steps forward to break Uke’s balance to the right rear corner.',
          'small_medium',
        ),
        c(
          'ju-uchi-oroshi-3',
          'Uke reverses his left hand with the fingertips facing downward to put his palm on Tori’s right elbow from the front side and pushes to turn around Tori’s body widely to his left side.',
          'small_medium',
        ),
        c(
          'ju-uchi-oroshi-4',
          'Tori: pivots on left foot, left hand grabs Uke’s left wrist with his left hand (from the top) and steps behind Uke, breaking Uke’s balance backwards.',
          'small_medium',
        ),
        c(
          'ju-uchi-oroshi-5',
          'Conclusion: Tori breaks Uke’s balance backward and applies Kansetsu-waza on Uke’s left arm with his left hand and Shime-waza with his right wrist at the same time.',
          'small_medium',
        ),
      ],
    },
    {
      id: 'ju-ryogan-tsuki',
      kind: 'technique',
      name: 'Ryo-gan-tsuki',
      nameJa: '両眼突',
      techniqueId: 'ju-ryogan-tsuki',
      groupId: 'dai-sankyo',
      criteria: [
        c(
          'ju-ryogan-tsuki-1',
          'Uke: raises his right hand and attacks Tori’s eyes by thrusting (fingers extended, ring and middle finger separated), stepping forward with right foot.',
          'small_medium',
        ),
        c(
          'ju-ryogan-tsuki-2',
          'Tori: turns to his left, avoids the thrust, and grabs Uke’s right wrist to pull the arm with his left hand.',
          'small_medium',
        ),
        c(
          'ju-ryogan-tsuki-3',
          'Uke: advances with his left foot and grabs Tori’s left wrist with his left hand to release his right arm.',
          'small_medium',
        ),
        c(
          'ju-ryogan-tsuki-4',
          'Tori: pushes Uke’s left elbow with his right palm in order to free his left arm.',
          'small_medium',
        ),
        c('ju-ryogan-tsuki-5', 'Uke: pivots around on the right foot.', 'small_medium'),
        c(
          'ju-ryogan-tsuki-6',
          'Tori: attacks Uke’s eyes by thrusting with left hand (fingers extended, ring and middle finger separated), stepping forward with his left foot.',
          'small_medium',
        ),
        c(
          'ju-ryogan-tsuki-7',
          'Uke: turns to his right, avoids the thrust and grabs Tori’s left wrist to pull the arm with his right hand.',
          'small_medium',
        ),
        c(
          'ju-ryogan-tsuki-8',
          'Tori: steps forward with his right foot and grabs Uke’s right wrist with his right hand to release his left arm.',
          'small_medium',
        ),
        c(
          'ju-ryogan-tsuki-9',
          'Uke: pushes Tori’s right elbow with his left palm in order to free his right arm, tries to turn Tori, but Tori puts his arm around Uke’s waist and lifts him with Uki-goshi (to O-Goshi).',
          'small_medium',
        ),
      ],
    },
  ],
}
